import { useCallback, useState, type ReactNode } from "react";
import { api } from "./api";
import type { BlockState } from "./types";

// ═══════════════════════════════════════════════════════════════════════════
//  BLOCKING AN ACCOUNT
// ═══════════════════════════════════════════════════════════════════════════
// The console's lever for an account that is abusing the API. It sits between
// the two it already had, both of which are the wrong tool:
//
//   end their sessions   they sign in again with an OTP in thirty seconds
//   delete the customer  irreversible, and destroys the evidence
//
// A block stops the account working and leaves everything about it in place.
//
// ── Why this is a form and not a confirm dialog ────────────────────────────
// `useRevoke` next door gets away with `window.confirm`, because the only
// decision is yes or no. A block is three decisions — for how long, and why,
// and whether it is permanent — and a prompt() chain for those would be worse
// than a form in every way. So this hook carries no UI of its own beyond the
// toast: the caller renders `BlockForm`, which expands in place in the table
// or panel, matching the invite and upload forms elsewhere in the console.
//
// ── The reason field is required and the API enforces it ───────────────────
// Not politeness. A block that somebody has to explain months later — to a
// customer on the phone, or to whoever is deciding whether to lift it — is
// worthless without the sentence that says what happened. The person who can
// write that sentence is the person clicking the button, and they will never
// write it afterwards.

/** What the form collects. Mirrors the API's body. */
export interface BlockRequest {
  reason: string;
  permanent: boolean;
  /** Ignored when `permanent`. */
  hours: number;
}

interface Blocker {
  block: (userId: string, name: string, req: BlockRequest) => Promise<boolean>;
  unblock: (userId: string, name: string, reason: string) => Promise<boolean>;
  /** The id currently in flight, for disabling that row's buttons. */
  busyId: string | null;
  toast: ReactNode;
}

/**
 * The durations offered, and the thinking behind the list.
 *
 * An hour is for "whatever that script is, make it stop now" and costs nothing
 * to be wrong about. A day covers a bad actor who will get bored. A week is
 * the longest that is still obviously temporary. Past that the honest answer
 * is a permanent block somebody can lift, not a date next year that nobody
 * will be here to see pass — which is also why the API caps a temporary block
 * at a year rather than accepting any number.
 */
export const BLOCK_DURATIONS = [
  { hours: 1, label: "1 hour" },
  { hours: 6, label: "6 hours" },
  { hours: 24, label: "24 hours" },
  { hours: 24 * 7, label: "7 days" },
  { hours: 24 * 30, label: "30 days" },
] as const;

export function useBlock(onDone?: () => void): Blocker {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  const flash = useCallback((text: string, error = false) => {
    setMessage({ text, error });
    window.setTimeout(() => setMessage(null), 6000);
  }, []);

  const block = useCallback(
    async (userId: string, name: string, req: BlockRequest) => {
      // The form already collected a reason and a duration, so the only thing
      // left to confirm is the one irreversible-feeling choice. A permanent
      // block is a decision about a person and deserves the extra beat; a
      // temporary one lifts itself and does not.
      if (req.permanent) {
        const ok = window.confirm(
          `Block ${name} permanently?\n\n` +
            `The account stops working immediately and stays blocked until ` +
            `an admin lifts it. Every signed-in device is signed out, and the ` +
            `customer is not notified — they will see "your account has been ` +
            `suspended" the next time they open the app.\n\n` +
            `Nothing is deleted. Recorded in the audit log against your account.`,
        );
        if (!ok) return false;
      }

      setBusyId(userId);
      try {
        const res = await api<{ sessionsEnded: number }>(
          `/api/admin/users/${userId}/block`,
          {
            method: "POST",
            body: req.permanent
              ? { permanent: true, reason: req.reason }
              : { hours: req.hours, reason: req.reason },
          },
        );
        const span = req.permanent
          ? "permanently"
          : `for ${BLOCK_DURATIONS.find((d) => d.hours === req.hours)?.label ?? `${req.hours} hours`}`;
        flash(
          `Blocked ${name} ${span}. ` +
            `${res.sessionsEnded} session${res.sessionsEnded === 1 ? "" : "s"} ended.`,
        );
        onDone?.();
        return true;
      } catch (err) {
        flash(
          err instanceof Error ? err.message : "Could not block that account.",
          true,
        );
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [flash, onDone],
  );

  const unblock = useCallback(
    async (userId: string, name: string, reason: string) => {
      setBusyId(userId);
      try {
        await api(`/api/admin/users/${userId}/unblock`, {
          method: "POST",
          body: { reason },
        });
        // Said plainly, because it is the part people get wrong: lifting a
        // block does not sign anybody back in. Revocation is one-way, so the
        // customer signs in again with an OTP like any returning user.
        flash(
          `Unblocked ${name}. They can sign in again with a fresh OTP — ` +
            `their old sessions stay ended.`,
        );
        onDone?.();
        return true;
      } catch (err) {
        flash(
          err instanceof Error ? err.message : "Could not lift that block.",
          true,
        );
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [flash, onDone],
  );

  const toast = message ? (
    <div
      className={message.error ? "toast toast-error" : "toast"}
      role="status"
      aria-live="polite"
    >
      {message.text}
    </div>
  ) : null;

  return { block, unblock, busyId, toast };
}

/**
 * The form itself — duration, reason, submit.
 *
 * Rendered by the caller wherever it makes sense: expanded under a table row,
 * or inside the panel on the customer page. It holds its own draft state, so
 * closing it discards a half-typed reason rather than carrying it to the next
 * customer somebody opens.
 */
export function BlockForm({
  name,
  busy,
  onSubmit,
  onCancel,
}: {
  name: string;
  busy: boolean;
  onSubmit: (req: BlockRequest) => void;
  onCancel: () => void;
}) {
  // 24 hours by default: long enough to stop something and short enough that
  // nobody has to remember to undo it.
  const [hours, setHours] = useState<number>(24);
  const [permanent, setPermanent] = useState(false);
  const [reason, setReason] = useState("");

  const ready = reason.trim().length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        onSubmit({ reason: reason.trim(), permanent, hours });
      }}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "flex-end",
        padding: "12px 0",
      }}
    >
      <div className="field" style={{ margin: 0, flex: "1 1 160px" }}>
        <label htmlFor={`block-span-${name}`}>How long</label>
        <select
          id={`block-span-${name}`}
          className="select"
          value={permanent ? "permanent" : String(hours)}
          onChange={(e) => {
            if (e.target.value === "permanent") {
              setPermanent(true);
            } else {
              setPermanent(false);
              setHours(Number(e.target.value));
            }
          }}
        >
          {BLOCK_DURATIONS.map((d) => (
            <option key={d.hours} value={String(d.hours)}>
              {d.label}
            </option>
          ))}
          <option value="permanent">Permanently</option>
        </select>
        <p className="field-hint">
          {permanent
            ? "Stays blocked until an admin lifts it."
            : "Lifts itself when the time is up — nobody has to remember."}
        </p>
      </div>

      <div className="field" style={{ margin: 0, flex: "2 1 280px" }}>
        <label htmlFor={`block-reason-${name}`}>Reason</label>
        <input
          id={`block-reason-${name}`}
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. scripted render calls, 4,000 in an hour"
          maxLength={500}
        />
        <p className="field-hint">
          Goes in the audit log and onto the account. The customer never sees
          it — write it for whoever reads this next.
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, paddingBottom: 18 }}>
        <button
          type="submit"
          className="btn btn-sm btn-danger"
          disabled={busy || !ready}
          title={ready ? undefined : "A reason is required"}
        >
          {busy ? "Blocking…" : permanent ? "Block permanently" : "Block"}
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The one place a block is turned into a badge, so it reads the same everywhere. */
export function blockLabel(state: BlockState | null): string | null {
  if (!state?.blocked) return null;
  if (state.permanent) return "Blocked";
  return "Suspended";
}
